import styled, { keyframes } from "@rentiohq/web-theme";

// The palette of the invitation page (public/rentio-connect.html), so the
// banner and the page it opens read as one piece.
export const COLORS = {
  green: "#11b981",
  greenDark: "#0d8b69",
  orange: "#ff7629",
  orangeDark: "#f0641a",
  mint: "#e8f8f2",
  peach: "#fff1e8",
  ink: "#153b35",
  muted: "#61736f",
  line: "#dce8e4",
};

const STACK_BREAKPOINT = "1240px";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const drift = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  50% {
    transform: translate(8px, -10px) rotate(4deg);
  }
  100% {
    transform: translate(-6px, 6px) rotate(-3deg);
  }
`;

// A sticker that sits still and shakes briefly every few seconds.
const wiggle = keyframes`
  0%, 88%, 100% {
    transform: rotate(-3deg) scale(1);
  }
  90% {
    transform: rotate(4deg) scale(1.06);
  }
  92% {
    transform: rotate(-5deg) scale(1.06);
  }
  94% {
    transform: rotate(4deg) scale(1.04);
  }
  96% {
    transform: rotate(-3deg) scale(1.02);
  }
`;

export const Banner = styled.section`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 32px;
  min-height: 176px;
  padding: 24px 36px 24px 170px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(7, 63, 54, 0.08);
  animation: ${fadeUp} 0.6s ease-out both;

  @media (max-width: ${STACK_BREAKPOINT}) {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 20px;
    padding: 24px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// The green shapes behind the content, one per corner, clipped by the banner.
export const Blob = styled.svg<{ delay: string; duration: string }>`
  position: absolute;
  z-index: 0;
  pointer-events: none;
  animation: ${drift} ${props => props.duration} ease-in-out infinite alternate;
  animation-delay: ${props => props.delay};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const BlobTopLeft = styled(Blob)`
  top: -60px;
  left: -50px;
  width: 230px;
  height: 170px;

  @media (max-width: ${STACK_BREAKPOINT}) {
    display: none;
  }
`;

export const BlobBottomLeft = styled(Blob)`
  bottom: -70px;
  left: -40px;
  width: 200px;
  height: 190px;
`;

export const BlobRight = styled(Blob)`
  top: -30px;
  right: -60px;
  width: 200px;
  height: 300px;

  @media (max-width: ${STACK_BREAKPOINT}) {
    display: none;
  }
`;

// The lockup: the real Rentio wordmark (SVG) with the hand-lettered
// "Connect" and "Insights & Drinks" from the design placed under it, at
// half the design's pixel size (the cutouts are 2x for retina).
export const Lockup = styled.div`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  width: 246px;
  height: 118px;
`;

export const LockupLogo = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 200px;
  height: auto;
`;

export const LockupConnect = styled.img`
  position: absolute;
  top: 55px;
  left: 126px;
  width: 120px;
  height: auto;
`;

export const LockupTagline = styled.img`
  position: absolute;
  top: 96px;
  left: 50px;
  width: 137px;
  height: auto;
`;

export const Info = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 14px;
  min-width: 240px;
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const Badge = styled.span<{ tint: string; color: string }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.tint};
  color: ${props => props.color};

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const Label = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${COLORS.muted};
`;

export const Value = styled.div`
  font-size: 19px;
  font-weight: 800;
  line-height: 1.2;
  color: ${COLORS.ink};
`;

export const Sub = styled.div`
  font-size: 14px;
  color: ${COLORS.muted};
`;

export const Divider = styled.div`
  align-self: stretch;
  width: 1px;
  background: ${COLORS.line};

  @media (max-width: ${STACK_BREAKPOINT}) {
    display: none;
  }
`;

export const Cta = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const Sticker = styled.img`
  width: 190px;
  height: auto;
  transform-origin: 50% 80%;
  animation: ${wiggle} 6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StickerText = styled.div`
  font-size: 24px;
  font-weight: 800;
  font-style: italic;
  color: ${COLORS.orange};
  transform-origin: 50% 80%;
  animation: ${wiggle} 6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  border-radius: 999px;
  background: ${COLORS.orange};
  color: #ffffff;
  font-size: 18px;
  font-weight: 800;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 8px 24px rgba(255, 118, 41, 0.28);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;
  }

  &:hover,
  &:focus-visible {
    background: ${COLORS.orangeDark};
    color: #ffffff;
    text-decoration: none;
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(255, 118, 41, 0.36);

    svg {
      transform: translateX(4px);
    }
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 118, 41, 0.4);
    outline-offset: 2px;
  }
`;
